import * as Sentry from '@sentry/react';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { destroyAccountInfoCookie, setAccountInfoCookie, TAccountInfo } from '@/common/cookie/accountInfo';
import { setJwt } from '@/common/cookie/jwt';
import { decode } from '@/common/jwt';
import { LoginMessage } from '@/components/pages/login/LoginMessage';
import { UNAUTHORIZED } from '@/constants/commons/statusCode';
import { RESERVATION_LIST_URL } from '@/constants/commons/url';
import { usePageSpecificCSS } from '@/hooks/commons/common/usePageSpecificCSS';
import { accountName, LoginTypeEnum, setAccountName, useLogin } from '@/hooks/pages/login/useLogin';
import { ConciergeLoginApi } from '@/infrastructures/api/concierges/ConciergeLoginApi';
import { TLwtApiResponse } from '@/infrastructures/api/LWTApi';
import { TJwtResponse } from '@/types/responses/login/Login';

export type TConciergeLoginRequestBody = {
  data: TAccountInfo;
};

export type TPropsForLoginPage = Readonly<TAccountInfo>;

export const LoginPresentation = (props: TPropsForLoginPage) => {
  const { accountName: initAccountName, password: initPassword } = props;

  const router = useRouter();
  const referer = decodeURIComponent((router.query.referer as string) ?? RESERVATION_LIST_URL);

  const [isLoginFailure, setIsLoginFailure] = useState(false);
  const [isWrongUserAccount, setIsWrongUserAccount] = useState(false);
  const [hasNotUserName, setHasNotUserName] = useState(false);
  const [hasNotPassword, setHasNotPassword] = useState(false);
  const clearInputError = () => {
    setHasNotUserName(false);
    setHasNotPassword(false);
  };

  useLogin(initAccountName, router.query.type as LoginTypeEnum);

  const handleOnChangeUserName = (e: React.ChangeEvent<HTMLInputElement>) => setAccountName(e.target.value);
  const [password, setPassword] = useState(initPassword);
  const handleOnChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);
  const [isSavingLoginInfo, setIsSavingLoginInfo] = useState(!!accountName.length && !!password.length);
  const handleOnChangeResisterSession = () => setIsSavingLoginInfo(!isSavingLoginInfo);

  const postLoginOnEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == 'Enter') {
      postLogin();
    }
  };

  const postLogin = async () => {
    if (isErrorInput()) return;

    const reqBody: TConciergeLoginRequestBody = {
      data: {
        accountName: accountName,
        password: password,
      },
    };
    const result = await ConciergeLoginApi.post(reqBody);

    const isUnauthorized = result.status === UNAUTHORIZED;
    if (isUnauthorized) {
      setIsWrongUserAccount(isUnauthorized);
      setEmptyInput();
      clearInputError();
      return;
    }

    const loginFailure = result.isError || !result.data;
    if (loginFailure) {
      setIsLoginFailure(loginFailure);
      setIsWrongUserAccount(false);
      return;
    }

    // resultはTLwtApiResponse<TConciergeJwt>のはずだが、TSでその判定ができていないため。
    const res = result as TLwtApiResponse<TJwtResponse>;
    const { jwt } = res.data;
    setJwt(jwt);

    const jwtDetail = decode(jwt);

    Sentry.setUser({
      conciergeId: `${jwtDetail.conciergeId}`,
      name: jwtDetail.name,
    });

    isSavingLoginInfo ? setAccountInfoCookie(accountName, password) : destroyAccountInfoCookie();

    router.push(referer);
  };

  const isErrorInput = () => {
    const isErrorInputUserName = !accountName;
    if (isErrorInputUserName) setHasNotUserName(isErrorInputUserName);
    const isErrorInputPassword = !password;
    if (isErrorInputPassword) setHasNotPassword(isErrorInputPassword);

    return isErrorInputUserName || isErrorInputPassword;
  };

  const setEmptyInput = () => {
    setAccountName('');
    setPassword('');
  };

  const LOGIN_INPUT_CLASS = 'login__input';
  const returnErrorUserNameClassName = () => {
    return isWrongUserAccount || hasNotUserName ? LOGIN_INPUT_CLASS + '__error' : '';
  };

  const returnErrorPasswordClassName = () => {
    return isWrongUserAccount || hasNotPassword ? LOGIN_INPUT_CLASS + '__error' : '';
  };

  const { CSSLinkTag, isLoadingCSS } = usePageSpecificCSS('login');

  return (
    <main className="main__login">
      {CSSLinkTag}
      {!isLoadingCSS && (
        <div className="login">
          <header className="login__header">
            <h1 className="login__title">LWT Concierge</h1>
            <p className="login__subtext">アカウントにログインしてください</p>
          </header>
          <LoginMessage />
          {isLoginFailure && (
            <p className="login__error">
              ログインに失敗しました。再度お試しください。
              <br />
              このメッセージが続く場合は管理者にお問い合わせください。
            </p>
          )}
          {isWrongUserAccount && <p className="login__error">ユーザー名またはパスワードが違います。</p>}

          <div className="login__field">
            <label className="login__label" htmlFor="username">
              ユーザー名
            </label>
            {hasNotUserName && <p className="login__error">ユーザー名は必須です。</p>}
            <input
              className={`${LOGIN_INPUT_CLASS} ${returnErrorUserNameClassName()}`}
              name="username"
              id="username"
              type="text"
              placeholder="ユーザー名"
              value={accountName}
              required
              onChange={handleOnChangeUserName}
              onKeyPress={postLoginOnEnter}
            />
          </div>
          <div className="login__field">
            <label className="login__label" htmlFor="pass">
              パスワード
            </label>
            {hasNotPassword && <p className="login__error">パスワードは必須です。</p>}
            <input
              className={`${LOGIN_INPUT_CLASS} ${returnErrorPasswordClassName()}`}
              name="password"
              id="pass"
              type="password"
              placeholder="パスワード"
              value={password}
              required
              onChange={handleOnChangePassword}
              onKeyPress={postLoginOnEnter}
            />
          </div>
          <div className="login__field login__field--center">
            <div className="login__checkbox">
              <input
                name="remember"
                id="remember"
                type="checkbox"
                onChange={handleOnChangeResisterSession}
                checked={isSavingLoginInfo}
              />
              <label htmlFor="remember">ログイン情報を記憶する</label>
            </div>
          </div>
          <button className="button button--green button--large" onClick={postLogin}>
            ログイン
          </button>
        </div>
      )}
    </main>
  );
};
